import { ChevronDownIcon } from '@assets/icons/ChevronIcon';
import { SearchIcon } from '@assets/icons/SearchIcon';
import { useProperty } from '@hooks/queries/useProperty';
import { loadAssetsAndImages } from '@redux/imageAssets/thunks';
import { selectRegion } from '@state/regions/region.selectors';
import { useBoundStore } from '@state/store';
import { Divider, Input, Radio, Tree } from 'antd';
import type { DataNode } from 'antd/es/tree';
import type { Key } from 'antd/lib/table/interface';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import type { RegionDTO } from '../../../../state/regions/region.models';
import { sortAlphabetically } from '../../../../utils';
import styles from './_regionTree.module.scss';

interface RegionDataNode extends DataNode {
  name: string;
}

interface HighlightPart {
  text: string;
  offset: number;
}

function HighlightedName({
  parts,
  name,
  searchValue,
}: Readonly<{ parts: HighlightPart[]; name: string; searchValue: string }>) {
  if (parts.length === 0) {
    return <span>{name}</span>;
  }

  return (
    <span>
      {parts.map(({ text, offset }) => (
        <span
          key={offset}
          className={text.toLowerCase() === searchValue.toLowerCase() ? styles.highlightText : ''}
        >
          {text}
        </span>
      ))}
    </span>
  );
}

export const RegionTree = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedRegion = useBoundStore(selectRegion);
  const { regionTree, setSelectedRegion } = useBoundStore((state) => state.regions);

  const { property } = useProperty();
  const regions = useBoundStore((state) => Object.values(state.regions.entities));

  const [searchValue, setSearchValue] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);

  const onSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.value) {
        const { value } = event.target;

        const newExpandedKeys = regions.reduce<Key[]>((acc, region) => {
          if (region.name.toLowerCase().includes(value.toLowerCase())) {
            acc.push(region.id);
          }

          return acc;
        }, []);

        setExpandedKeys(newExpandedKeys);
        setSearchValue(value);
      } else {
        setExpandedKeys([]);
        setSearchValue('');
      }
    },
    [regions]
  );

  const onClickParent = useCallback(
    (value: string) => {
      const isExpanded = expandedKeys.includes(value);

      if (isExpanded) {
        setExpandedKeys(expandedKeys.filter((key) => key !== value));
      } else {
        setExpandedKeys([...expandedKeys, value]);
      }
    },
    [expandedKeys]
  );

  const onClickLeaf = useCallback(
    (regionId?: string) => {
      if (property?.id && regionId) {
        setSelectedRegion(regionId);
        dispatch(loadAssetsAndImages(property.id));
      }
    },
    [dispatch, property, setSelectedRegion]
  );

  const treeData = useMemo((): RegionDataNode[] => {
    const generateParentNode = (
      region: RegionDTO,
      children: RegionDataNode[],
      highlightText: HighlightPart[]
    ): RegionDataNode => {
      return {
        title: (
          <button type="button" onClick={() => onClickParent(region.id)}>
            <HighlightedName parts={highlightText} name={region.name} searchValue={searchValue} />
          </button>
        ),
        name: region.name,
        key: region.id,
        children,
      };
    };

    const generateChildNode = (
      region: RegionDTO,
      highlightText: HighlightPart[]
    ): RegionDataNode => {
      return {
        title: (
          <button type="button" onClick={() => onClickLeaf(region.id)}>
            <HighlightedName parts={highlightText} name={region.name} searchValue={searchValue} />
          </button>
        ),
        name: region.name,
        key: region.id,
        children: [],
        switcherIcon: (
          <Radio
            onClick={() => onClickLeaf(region.id)}
            checked={selectedRegion?.id === region.id}
          />
        ),
      };
    };

    const getHighlightText = (region: RegionDTO): HighlightPart[] => {
      if (!region.name.toLowerCase().includes(searchValue.toLowerCase())) {
        return [];
      }

      const segments = searchValue
        ? region.name.split(new RegExp(`(${searchValue})`, 'gi'))
        : [region.name];

      let offset = 0;

      return segments.reduce<HighlightPart[]>((acc, text) => {
        if (text) {
          acc.push({ text, offset });
        }
        offset += text.length;

        return acc;
      }, []);
    };

    const generateTreeNodes = (regions: RegionDTO[] = [], parentHighlight?: boolean) => {
      const treeNodes = regions.reduce<RegionDataNode[]>((acc, region) => {
        const highlightText = getHighlightText(region);

        // Parent
        if (region.children_region.length) {
          const children = generateTreeNodes(
            region.children_region,
            highlightText.length > 0 || parentHighlight
          );

          if (children.length) {
            const parentNode = generateParentNode(region, children, highlightText);
            acc.push(parentNode);
          }

          return acc;
        }

        // Child
        if (highlightText.length > 0 || parentHighlight) {
          const childNode = generateChildNode(region, highlightText);
          acc.push(childNode);
        }

        return acc;
      }, []);

      return treeNodes.toSorted(sortAlphabetically);
    };

    if (regionTree) {
      return generateTreeNodes(regionTree.children_region);
    }

    return [];
  }, [onClickLeaf, onClickParent, regionTree, searchValue, selectedRegion?.id]);

  return (
    <div className="max-w-[320px] max-h-[411px] flex flex-col">
      <div className="mx-3 my-2 border-b border-sy-neutral-30">
        <Input
          placeholder={t('header.regions.placeholder')}
          onChange={onSearch}
          bordered={false}
          suffix={<SearchIcon width={18} height={18} />}
          className="border-b"
        />
      </div>
      <div className="px-3 py-1.5 font-semibold">
        <Radio
          onClick={() => onClickLeaf(regionTree?.id)}
          id="all-regions"
          checked={selectedRegion?.id === regionTree?.id}
        />
        <label htmlFor="all-regions">{t('header.regions.all')}</label>
        <Divider className="my-2" />
      </div>
      <div className="px-2">
        <Tree
          height={306}
          style={{ width: 320 }}
          autoExpandParent={true}
          selectable={false}
          className={styles.tree}
          expandedKeys={expandedKeys}
          onExpand={setExpandedKeys}
          treeData={treeData}
          switcherIcon={<ChevronDownIcon />}
        />
      </div>
    </div>
  );
};

export default RegionTree;
